// A confirmation or a rejection applied to the Snowflake map the page already holds (module 04 DESIGN
// B395), in place of reading the list again. The list's two twelve-month aggregates cost about half a
// second as the app role on the live store, and the confirmation run is one press for each of up to two
// hundred rows; nothing in that read changes when a row is confirmed except the row.
//
// Nothing here decides anything. The API's answer says what was written on the row (who, when, the
// version), in the list's own words, and this puts it on the row, moves the row to where the list
// would have put it, and counts again by the API's own rule (apps/api/src/admin/snowflake.ts mapView).
//
// NOT for a rejected COMPANY row: that one decision changes other rows (a rejected row's figures stop
// counting on its company's other rows, and the company's place in the order moves with them), so the
// page reads the list again after it, as it always has.
import type { CompetitionRow, MapCounts, OrgMapRow } from './api.js';

export interface Decided {
  kind: string; verb: string; id: string; version: number;
  confirmedBy: string | null; confirmedAt: string | null; rejectedAt: string | null;
}

export interface MapData {
  organisations: { counts: MapCounts; rows: OrgMapRow[] };
  competitions: { counts: MapCounts; rows: CompetitionRow[] };
}

type Row = OrgMapRow | CompetitionRow;
const SECTION = { candidate: 0, confirmed: 1, rejected: 2 } as const;

/** Whether the API's answer carries the decision. One from before B395 does not, and the page reads the list again instead. */
export function carriesDecision(d: Partial<Decided> | undefined): d is Decided {
  return !!d && typeof d.id === 'string' && (typeof d.confirmedAt === 'string' || typeof d.rejectedAt === 'string');
}

/**
 * The list is what needs a person, then what was confirmed, then what was rejected, and inside each by
 * the row's `place` (the list's own order, computed by its query). The decided row goes before the
 * first row that belongs after it, which is where reading the list again would show it.
 */
function moved<T extends Row>(rows: T[], next: T): T[] {
  const rest = rows.filter((r) => r.id !== next.id);
  const at = rest.findIndex((r) => SECTION[r.state] > SECTION[next.state] || (r.state === next.state && r.place > next.place));
  return at === -1 ? [...rest, next] : [...rest.slice(0, at), next, ...rest.slice(at)];
}

/** mapView's counts: linked is distinct companies (or rights) with a confirmed row, the other two are rows. */
function counted<T extends Row>(inStore: number, rows: T[], target: (r: T) => string): MapCounts {
  return {
    inStore,
    linked: new Set(rows.filter((r) => r.state === 'confirmed').map(target)).size,
    candidates: rows.filter((r) => r.state === 'candidate').length,
    rejected: rows.filter((r) => r.state === 'rejected').length,
  };
}

function decideIn<T extends Row>(part: { counts: MapCounts; rows: T[] }, d: Decided, target: (r: T) => string): { counts: MapCounts; rows: T[] } {
  const row = part.rows.find((r) => r.id === d.id);
  if (!row) return part;
  const next: T = {
    ...row, version: d.version, confirmedBy: d.confirmedBy, confirmedAt: d.confirmedAt, rejectedAt: d.rejectedAt,
    state: d.confirmedAt ? 'confirmed' : d.rejectedAt ? 'rejected' : 'candidate',
  };
  const rows = moved(part.rows, next);
  return { counts: counted(part.counts.inStore, rows, target), rows };
}

export function applyDecision<T extends MapData>(data: T, kind: 'organisation' | 'competition', d: Decided): T {
  return kind === 'organisation'
    ? { ...data, organisations: decideIn(data.organisations, d, (r) => r.organisationId) }
    : { ...data, competitions: decideIn(data.competitions, d, (r) => r.rightsPropertyId) };
}
